"use client";
import {
  faCommentDots,
  faEnvelope,
  faFileSignature,
  faKey,
  faPenToSquare,
  faRightFromBracket,
  faWindowMaximize,
} from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import React, { useCallback, useMemo } from "react";
import { useRecoilValue } from "recoil";

import UserInfoMenu from "components/UserInfoMenu";
import useLogout from "hooks/useLogout";
import { unreadMessageStore } from "store/unreadMessageStore";
import { cn } from "utils/cn";

const NavItem = ({ name, path, isActive }: { name: string; path: string; isActive: boolean }) => {
  const classes = clsx(
    "flex items-center relative hover-underline px-2",
    isActive ? "underline-active text-black-light" : "text-gray"
  );
  const unreadMessage = useRecoilValue(unreadMessageStore);
  return (
    <Link href={path} className={classes}>
      <li>{name}</li>
      <div className="absolute bottom-0 left-0 h-0 underline-element"></div>
      {unreadMessage && path === "/customer" && (
        <div
          className={cn("absolute right-[-10px] top-3 h-2 w-2 rounded-full bg-attention", !isActive && "hidden")}
        ></div>
      )}
    </Link>
  );
};

const NAV_ITEMS = [
  {
    name: "ホーム",
    path: "/dashboard",
  },
  {
    name: "物件管理",
    path: "/property",
  },
  {
    name: "顧客管理",
    path: "/customer",
  },
  {
    name: "売上管理",
    path: "/sales",
  },
];

const Header = () => {
  const pathname = usePathname();
  const router = useRouter();

  const onNavItemClick = useCallback(
    (path: string) => {
      router.push(path);
    },
    [router]
  );

  const onNavItemHover = useCallback(
    (path: string) => {
      router.prefetch(path);
    },
    [router]
  );

  const { logout } = useLogout();

  const pathIncludesString = useCallback(
    (path: string) => {
      const escapeRegExp = (text: string) => {
        return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
      };

      const escapedPattern = escapeRegExp(path);
      const regexPattern = new RegExp(`^${escapedPattern}(\\/|$)`);

      return regexPattern.test(pathname);
    },
    [pathname]
  );

  const menuList = useMemo(
    () => [
      {
        label: "サイト情報設定",
        onClickMenuItem: () => onNavItemClick("/site-config/update"),
        onHoverMenuItem: () => onNavItemHover("/site-config/update"),
        icon: faWindowMaximize,
      },
      {
        label: "申込項目設定",
        onClickMenuItem: () => onNavItemClick("/form-setting"),
        onHoverMenuItem: () => onNavItemHover("/form-setting"),
        icon: faPenToSquare,
      },
      {
        label: "通知グループ設定",
        onClickMenuItem: () => onNavItemClick("/notification-group"),
        onHoverMenuItem: () => onNavItemHover("/notification-group"),
        icon: faEnvelope,
      },
      {
        label: "メッセージ設定",
        onClickMenuItem: () => onNavItemClick("/message-setting"),
        onHoverMenuItem: () => onNavItemHover("/message-setting"),
        icon: faCommentDots,
      },
      {
        label: "契約書名義設定",
        onClickMenuItem: () => onNavItemClick("/signature-setting"),
        onHoverMenuItem: () => onNavItemHover("/signature-setting"),
        icon: faFileSignature,
      },
      {
        label: "パスワード変更",
        onClickMenuItem: () => onNavItemClick("/password/change"),
        onHoverMenuItem: () => onNavItemHover("/password/change"),
        icon: faKey,
      },
      {
        label: "ログアウト",
        onClickMenuItem: () => logout(),
        onHoverMenuItem: () => onNavItemHover("/login"),
        icon: faRightFromBracket,
      },
    ],
    [onNavItemClick, logout, onNavItemHover]
  );

  return (
    <nav className="flex h-[60px] w-full flex-row justify-between bg-white shadow-card">
      <ul className="flex items-center">
        <li className="flex h-full border-r border-border py-[11px] pl-4 pr-[15px]">
          <img src="/svg/logo.svg" alt="Home" className="h-[38px] w-[129.87px] border-normal" />
        </li>
        <div className="ml-8 flex h-[100%] space-x-8">
          {NAV_ITEMS.map(({ name, path }) => (
            <NavItem key={name} name={name} path={path} isActive={pathIncludesString(path)} />
          ))}
        </div>
      </ul>
      <div className="flex items-center">
        <Link
          href="https://landit-inc.notion.site/SYNC-PORT-1846e565ac0a80259228df1c4a87b5d3"
          target="_blank"
          rel="noopener noreferrer"
          className="-mr-10 flex h-full items-center pl-2 pr-6 text-s text-gray"
        >
          利用ガイド
        </Link>
        <UserInfoMenu menuList={menuList} />
      </div>
    </nav>
  );
};

export default Header;
